<template>
  <div v-if="badges && badges.length" class="emh-campaign-tags">
    <!-- option A -->
    <div v-if="showOptionA" class="emh-campaign-tags__default-row">
      <!-- single campaign -->
      <template v-if="badges.length === 1">
        <wb-tag
          class="emh-campaign-tags__badge wb-tag"
          :variant.attr="'primary'"
          :title.attr="badges[0].name"
          :style="{
            cursor: campaignBadgesClickable ? 'pointer' : 'default',
          }"
          @click.prevent.stop="openModal"
        >
          <custom-icon name="campaign" class="emh-campaign-tags__badge-icon" />
          {{ badges[0].name }}&nbsp;<footnote-reference
            v-if="badges[0].footnote"
            :text="badges[0].footnote"
            :reference="generateReference('optionA')"
          />
        </wb-tag>
      </template>
      <!-- multiple campaign -->
      <wb-tag
        v-if="badges.length > 1"
        class="wb-tag wb-tag--primary"
        :variant.attr="'primary'"
        @click.prevent.stop="openModal"
      >
        <custom-icon name="campaign" class="emh-campaign-tags__badge-icon" />
        <span class="emh-campaign-tags__badge-count">{{ $t('dcp.campaignTags.all', { count: badges.length }) }}</span>
      </wb-tag>
    </div>
    <!-- optionB mobile -->
    <div v-if="!showOptionA && isMobileViewport" class="emh-campaign-tags__default-row">
      <!-- single campaign -->
      <template v-if="badges.length === 1">
        <wb-tag
          class="emh-campaign-tags__badge wb-tag"
          :variant.attr="'secondary'"
          :title.attr="badges[0].name"
          :style="{
            cursor: campaignBadgesClickable ? 'pointer' : 'default',
          }"
          @click.prevent.stop="openModal"
        >
          {{ badges[0].name }}&nbsp;<footnote-reference
            v-if="badges[0].footnote"
            :text="badges[0].footnote"
            :reference="generateReference('optionBMobile')"
          />
        </wb-tag>
      </template>
      <!-- multiple campaign -->
      <wb-tag v-if="badges.length > 1" class="wb-tag" :variant.attr="'primary'" @click.prevent.stop="openModal">
        <custom-icon name="campaign" class="emh-campaign-tags__badge-icon" />
        <span class="emh-campaign-tags__badge-count">{{ $t('dcp.campaignTags.all', { count: badges.length }) }}</span>
      </wb-tag>
    </div>
    <!-- optionB desktop -->
    <div v-if="!showOptionA && !isMobileViewport" class="emh-campaign-tags__default-row">
      <wb-tag
        class="emh-campaign-tags__badge wb-tag"
        :variant.attr="'secondary'"
        :title.attr="badges[0].name"
        :style="{
          cursor: campaignBadgesClickable ? 'pointer' : 'default',
        }"
        @click.prevent.stop="openModal"
        >{{ badges[0].name }}&nbsp;<footnote-reference
          v-if="badges[0].footnote"
          :text="badges[0].footnote"
          :reference="generateReference('optionBDesktop')"
        />
      </wb-tag>
      <wb-tag
        v-if="badges.length > 1"
        class="emh-campaign-tags__btn-toggle wb-type-hint-strong wb-tag"
        :class="{ '-collapsed': unwrapped }"
        :variant.attr="'primary'"
        @click.prevent.stop="handleBtnClick"
        @mouseenter="openWrap"
        @mouseleave="canExpand = true"
      >
        <Transition name="close-button" mode="out-in">
          <wb-icon v-if="unwrapped" :name.attr="'close'" class="emh-campaign-tags__badge-icon" />
        </Transition>
        <Transition name="badge-count" mode="out-in">
          <span v-if="!unwrapped" class="emh-campaign-tags__badge-count">{{
            $t('dcp.campaignTags.more', { count: badges.length - 1 })
          }}</span>
        </Transition>
      </wb-tag>
    </div>
    <TransitionGroup
      v-if="!showOptionA && !isMobileViewport"
      name="badges-expand"
      tag="div"
      :appear="true"
      class="emh-campaign-tags__unwrapped-list"
    >
      <wb-tag
        v-for="(badge, index) in extraBadges"
        :key="badge.name"
        class="emh-campaign-tags__badge wb-tag"
        :variant.attr="'secondary'"
        :title.attr="badge.name"
        :style="{
          cursor: campaignBadgesClickable ? 'pointer' : 'default',
          'animation-duration': `${0.1 + 0.1 * index}s`,
        }"
        @click.prevent.stop="openModal"
      >
        {{ badge.name }}&nbsp;<footnote-reference
          v-if="badge.footnote"
          :text="badge.footnote"
          :reference="generateReference('extraBadge', index)"
        />
      </wb-tag>
    </TransitionGroup>
    <!-- campaign modal for both options -->
    <Teleport to="#emh-modal-target">
      <campaign-modal v-if="modalOpen" :campaigns="badges" @close="closeModal"></campaign-modal>
    </Teleport>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref } from 'vue';
import { DCPBadgeDTO } from '@dh-io-emh/commerce-api/src/models/shared/ocb/api.schema';
import { useFootnoteReference } from '../../composables/footnote-reference.composable';
import { infuse } from '../../ioc-config';
import { onWindowResize, viewPortService } from '@dh-io-emh/ui/src/utils/viewport.util';
import CampaignModal from '../campaignModal/index.vue';
import FootnoteReference from '../footnotes/footnoteReference.vue';
import CustomIcon from '@dh-io-emh/ui/src/elements/customIcon/CustomIcon.vue';

export default defineComponent({
  name: 'CampaignTags',
  components: {
    FootnoteReference,
    CustomIcon,
    CampaignModal,
  },
  props: {
    badges: {
      type: Array as PropType<DCPBadgeDTO[]>,
      required: true,
    },
    option: {
      type: String,
      default: 'A',
    },
  },
  emits: ['modal-open', 'expand'],
  setup(props, { emit }) {
    const { campaignBadgesClickable } = infuse('config');
    const { generateReference } = useFootnoteReference('campaignTags');
    const showOptionA = computed(() => props.option === 'A');
    const unwrapped = ref(false);
    const canExpand = ref(true);
    const modalOpen = ref(false);

    const extraBadges = computed(() => (unwrapped.value ? props.badges.slice(1) : []));

    const isMobileViewport = ref<boolean>(viewPortService.isMobile());

    onWindowResize(() => {
      isMobileViewport.value = viewPortService.isMobile();
    });

    const openWrap = (e: MouseEvent) => {
      const timer = setTimeout(expand, 300);

      if (unwrapped.value) canExpand.value = false;

      // do not open if mouseleave
      e.target?.addEventListener('mouseleave', () => clearTimeout(timer), { once: true });
    };

    const openModal = () => {
      if (!campaignBadgesClickable) return;

      modalOpen.value = true;
      emit('modal-open');
    };

    const closeModal = () => {
      modalOpen.value = false;
    };

    const expand = () => {
      // make sure the cursor has left before expanding again
      // otherwise it can't be collapsed via click again
      if (!canExpand.value) return;

      unwrapped.value = true;
      canExpand.value = false;
      emit('expand');
    };

    const handleBtnClick = () => {
      if (unwrapped.value) {
        unwrapped.value = false;
      } else {
        canExpand.value = true;
        expand();
      }
    };

    return {
      campaignBadgesClickable,
      canExpand,
      expand,
      emit,
      extraBadges,
      handleBtnClick,
      openWrap,
      openModal,
      closeModal,
      modalOpen,
      unwrapped,
      showOptionA,
      isMobileViewport,
      generateReference,
    };
  },
});
</script>
<style src="./campaignTags.scss" lang="scss"></style>
